import React, { useEffect, useState } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import 'theme/iconClasses.css';

import { primaryColor } from 'theme/theme';
import { ApplicationState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import PageSpinner from 'components/spinners/PageSpinner';
import { SawmillCard } from './SawmillCard';
import { CreateEditSawmillDialog } from './CreateEditSawmillDialog';
import { SawmillResponse } from '.generated/api';
import { sawmillActions } from 'actions/sawmillActions';
import { useParams } from 'react-router';
import ConfirmDialog from 'components/ConfirmDialog';

const useStyles = makeStyles((theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  marginRight: {
    marginRight: '0.6rem',
  },

  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  addButton: { margin: 0, marginTop: theme.spacing(1) },
  actionsButton: {
    padding: '0',
  },
  buttonRoot: {
    marginRight: 0,
  },
  tableContainer: {
    marginTop: '1rem',
    marginLeft: '-1.5rem',
    width: 'calc(100% + 3rem)',
  },
  table: {
    color: primaryColor(),
  },
  tableHead: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    color: primaryColor(),
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  paper: {
    padding: '0',
  },
}));

const SawmillsTab: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const { orgId } = useParams<{ orgId: string }>();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { sawmills } = useSelector((state: ApplicationState) => state.sawmills);
  const [activeSawmill, setActiveSawmill] = useState<
    SawmillResponse | undefined
  >();
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [confirmSawmillDelete, setConfirmSawmillDelete] = useState<
    SawmillResponse | undefined
  >();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleDialogClose = () => {
    if (!deleteInProgress) {
      setDialogOpen(false);
      setActiveSawmill(undefined);
    }
  };

  const handleEditSawmill = (sawmill: SawmillResponse) => {
    setActiveSawmill(sawmill);
    setDialogOpen(true);
  };

  const handleDeleteSawmillClicked = (sawmill: SawmillResponse) => {
    setConfirmSawmillDelete(sawmill);
    setDeleteInProgress(false);
  };

  const handleDeleteSawmillConfirmed = () => {
    if (confirmSawmillDelete) {
      setDeleteInProgress(true);
      dispatch(
        sawmillActions.deleteSawmill(confirmSawmillDelete.id, () => {
          setConfirmSawmillDelete(undefined);
        }),
      );
    }
  };

  const handleDeleteSawmillCanceled = () => {
    setConfirmSawmillDelete(undefined);
  };

  useEffect(() => {
    if (orgId) {
      dispatch(sawmillActions.getSawmillsByOrganizationId(orgId));
    }
  }, [orgId]);
  return (
    <>
      <PageSpinner isLoading={sawmills === undefined}>
        <Grid container direction="column" spacing={4}>
          <Grid item>
            <div className={classes.alignRight}>
              <Button
                onClick={() => setDialogOpen(true)}
                variant="contained"
                color="primary"
                className={classes.addButton}
              >
                <span className={classes.plusIcon}>+</span>

                {t('organization_page.sawmills.labels.add_sawmill')}
              </Button>
            </div>
          </Grid>
          {sawmills &&
            sawmills.map((sawmill) => (
              <Grid item key={sawmill.id}>
                <SawmillCard
                  sawmill={sawmill}
                  onEdit={handleEditSawmill}
                  onDelete={handleDeleteSawmillClicked}
                />
              </Grid>
            ))}
          <CreateEditSawmillDialog
            isOpen={isDialogOpen}
            handleClose={handleDialogClose}
            sawmill={activeSawmill}
          />
        </Grid>
      </PageSpinner>
      <ConfirmDialog
        isOpen={!!confirmSawmillDelete}
        isLoading={deleteInProgress}
        title={t('organization_page.sawmills.dialog.delete_sawmill_title')}
        text={t('organization_page.sawmills.dialog.delete_sawmill_text', {
          sawmill: confirmSawmillDelete?.name,
        })}
        confirmButtonText={t('common.delete')}
        onConfirm={handleDeleteSawmillConfirmed}
        onCancel={handleDeleteSawmillCanceled}
      />
    </>
  );
};

export { SawmillsTab };
